import Currency from 'helpers/Currency';
import { parseMediaUrl } from 'helpers/File';
import { isObject } from 'helpers/Object';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import mapKeys from 'lodash/mapKeys';
import { action, extendObservable, toJS } from 'mobx';
import { ModuleType } from 'modules/content/moduleEditor/components/select/ModuleTypes';
import { FileService } from 'modules/file';
import { ENTITY_TYPE } from 'modules/file/constants';
import { isVideo } from 'modules/file/helpers';
import { MembershipStore } from 'modules/membership';
import { UiService } from 'modules/ui';

import Store from './ModuleEditorStore';

import '../ContentApi';

import Request from '@hotmart/request';

class ModuleEditorService {
  // #region setup
  @action
  init(data = {}, history) {
    this.clear();
    this.setData(data);
    this.history = history;
  }

  @action
  clear() {
    Object.assign(Store, {
      id: null,
      name: '',
      classes: [],
      extra: false,
      publicModule: MembershipStore.hasSeason,
      moduleType: null,
      paidModule: false,
      description: '',
      offerPrice: '',
      playerMedia: null,
      mediaVideoUrl: null,
      coverImageId: null,
      coverImageUrl: null,
      image: null,
      media: null,
      season: null,
      hotPassCost: null,
      loaded: true,
      snapshot: {}
    });
  }

  @action
  setData(data = {}) {
    extendObservable(Store, data);
  }
  // #endregion

  // #region actions
  @action
  async get(id) {
    if (!id) {
      return;
    }

    Store.loaded = false;
    let response = null;

    try {
      const medias = await FileService.fetchModuleMedia(id);
      const images = await FileService.fetchModuleImage(id);
      const cover = await FileService.fetchByType(
        id,
        ENTITY_TYPE.MODULE_COVER_IMG
      );

      response = await Request('module.get', { id });
      response.media = medias[0];
      response.image = images[0];
      response.cover = cover;

      response = this.filterMediasByPriority(response);

      const moduleFormatted = await this.formatReceivedModule(response);

      this.setData(moduleFormatted);
      this.snapshot = this.current;
    } catch (error) {
      this.handleError(error);
    } finally {
      Store.loaded = true;
    }

    return response;
  }

  @action
  async createItem(module) {
    return await Request('module.create', module);
  }

  @action
  async create(module) {
    if (!module || isEmpty(module)) {
      return;
    } else {
      module = this.formatModule(module);
    }

    Store.loaded = false;
    let response = null;

    try {
      response = await this.createItem(module);
      this.setData({ ...response });
      UiService.setAlertSuccess('done');
      this.snapshot = this.current;
    } catch (error) {
      this.handleError(error);
      response = false;
    } finally {
      Store.loaded = true;
    }

    return response;
  }

  @action
  async update(module) {
    if (!module || isEmpty(module)) {
      return;
    } else {
      module = this.formatModule(module);
    }

    Store.loaded = false;
    let response = null;

    try {
      response = await Request('module.update', module);
      UiService.setAlertSuccess('done');
      this.snapshot = this.current;
      response = true;
    } catch (error) {
      this.handleError(error);
      response = false;
    } finally {
      Store.loaded = true;
    }

    return response;
  }
  // #endregion

  // #region getters & setters
  get mediaVideoUrl() {
    return Store.mediaVideoUrl;
  }

  set mediaVideoUrl(value) {
    Store.mediaVideoUrl = value;
  }

  set moduleDisplayType(value) {
    Store.moduleDisplayType = value;
  }

  set coverImageId(value) {
    Store.coverImageId = value;
  }

  set coverImageUrl(value) {
    Store.coverImageUrl = value;
  }

  get extra() {
    return Store.extra;
  }

  get snapshot() {
    return Store.snapshot;
  }

  set snapshot(value) {
    Store.snapshot = value;
  }

  get current() {
    return cloneDeep(this.formatModule(Store));
  }
  // #endregion

  // #region helpers
  formatModule(module) {
    const { id, extra, season, moduleType } = Store;
    const {
      name,
      classes,
      publicModule = MembershipStore.hasSeason,
      paidModule = moduleType === ModuleType.Paid,
      hotPassCost = null
    } = module;

    module.mediasObj = this.mediasObj(module);

    const moduleFormatted = Object.assign(
      {},
      {
        name,
        classes: toJS(classes),
        extra,
        publicModule,
        paidModule,
        imageId: module.mediasObj.imageId,
        mediaId: module.mediasObj.mediaId,
        purchaseData: this.formatPurchaseData({ ...module, paidModule }),
        coverImageId: module.coverImageId
      }
    );

    if (id) {
      moduleFormatted.id = id;
    }

    if (season) {
      moduleFormatted.season = season;
      moduleFormatted.hotPassCost = this.hotPassCostInt(hotPassCost);
    }

    return moduleFormatted;
  }

  formatPurchaseData({
    paidModule = false,
    description,
    offerPrice = null,
    mediasObj = {}
  }) {
    if (paidModule) {
      return {
        description,
        offerPrice: Currency.currencyStringToNumber(offerPrice),
        mediaVideoUrl: mediasObj.mediaVideoUrl,
        mediaVideoProvider: mediasObj.mediaVideoProvider
      };
    }
  }

  mediasObj(module) {
    const { mediaVideoUrl } = Store;
    const { media } = module;

    const mediasObj = {
      mediaId: null,
      imageId: null,
      mediaVideoUrl: null,
      mediaVideoProvider: null
    };

    if (!isEmpty(media)) {
      if (isVideo(media)) {
        mediasObj.mediaId = this.formatMediaId(media);
      } else {
        mediasObj.imageId = this.formatMediaId(media);
      }
    }

    if (!isEmpty(mediaVideoUrl)) {
      mediasObj.mediaVideoUrl = mediaVideoUrl;
      mediasObj.mediaVideoProvider = parseMediaUrl(mediaVideoUrl).type;
    }

    return mediasObj;
  }

  formatMediaId = (media) => media.id || media.fileId;

  hotPassCostInt(hotPassCost) {
    if (!hotPassCost) {
      return null;
    }

    const hotPassCostInt = parseInt(hotPassCost.replace(/_/g, ''));

    return hotPassCostInt > 0 ? hotPassCostInt : null;
  }

  async formatReceivedModule(module) {
    module.moduleType = module.extra ? ModuleType.Extra : ModuleType.Standard;
    module.hotPassCost = module.hotPassCost ? String(module.hotPassCost) : null;

    module.media = module.media || module.image;
    delete module.image;

    if (module.purchaseData) {
      module.description = module.purchaseData.description;
      module.offerPrice = Currency.numberToCurrencyString(
        module.purchaseData.offerPrice
      );
      module.mediaVideoUrl = module.purchaseData.mediaVideoUrl;
      module.mediaVideoProvider = module.purchaseData.mediaVideoProvider;

      if (module.paidModule) {
        module.moduleType = ModuleType.Paid;
      }

      delete module.purchaseData;
    }

    if (module.cover.length) {
      module.coverImageId = module.cover[0].id;
      module.coverImageUrl = module.cover[0].thumborSignedUrl;

      delete module.cover;
    }

    return module;
  }

  filterMediasByPriority(response) {
    if (!isEmpty(response.media)) {
      if (!response.purchaseData) {
        response.purchaseData = {};
      }

      response.purchaseData.mediaVideoUrl = void 0;
      response.purchaseData.mediaVideoProvider = void 0;
      response.image = void 0;
    } else if (
      response.purchaseData &&
      !isEmpty(response.purchaseData.mediaVideoUrl)
    ) {
      response.image = void 0;
    }

    return response;
  }

  mapErrorDetail(errorDetail) {
    const errorDetailMap = {
      'purchaseData.description': 'description'
    };

    return mapKeys(errorDetail, (_, key) => errorDetailMap[key] || key);
  }

  handleError(error = {}) {
    const { data } = error;
    const invalidHashError =
      isObject(data) && data.errorCode && data.errorCode === '0007';

    if (invalidHashError) {
      this.history?.replace('new', { id: 'new' });
    }

    if (error?.data?.errorDetail) {
      error.data.errorDetail = this.mapErrorDetail(error.data.errorDetail);
    }

    UiService.handleError(error);
  }
  // #endregion
}

export default new ModuleEditorService();
